import { login, getUserDetails } from 'util/api'
import { useDocTitle } from 'util/hooks'

import React, { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import 'css/pages/login.css'

export const Login = () => {
	const navigate = useNavigate()

	const [ username, setUsername ] = useState()
	const [ password, setPassword ] = useState()
	const [ error, setError ] = useState(null)

	// Redirect to dashboard if already logged in
	useEffect(() => {
		const loggedIn = localStorage.getItem('loggedIn')
		const expiration = new Date(30 * 24 * 60 * 60 * 1000).getTime()
		if (loggedIn && Date.now() - loggedIn < expiration) {
			const isStudent = localStorage.getItem('studentId')
			navigate(isStudent ? '/enter-code' : '/dashboard', { replace: true })
		}
	}, [navigate])

	const onUsernameBlur = ({ target }) => {
		setUsername(target.value)
	}

	const onPasswordBlur = ({ target }) => {
		setPassword(target.value)
	}

	const onFormSubmit = async event => {
		event.preventDefault()

		// err-12: a network failure used to reject unhandled and do NOTHING
		let response
		try {
			response = await login(username, password)
		} catch (e) {
			setError("We couldn't reach the server. Check your internet connection and try again.")
			return
		}

		if (response.status === 200) {
			localStorage.setItem('loggedIn', Date.now())
			// After login, check account type to route students to enter code
			try {
				const details = await getUserDetails()
				if (details?.accountType === 'student') {
					// Persist student info for later use in student flows
					if (details?.userId) localStorage.setItem('studentId', details.userId)
					if (details?.firstName) localStorage.setItem('firstName', details.firstName)
					navigate('/enter-code')
					return
				}
				// Ensure studentId is cleared for non-student accounts
				localStorage.removeItem('studentId')
			} catch (e) {
				// fallback to dashboard on failure
				localStorage.removeItem('studentId')
			}
			navigate('/dashboard')
			return
		}

		// Handle Cognito challenges
		if (response.status === 401) {
			const body = await response.json().catch(() => ({}))
			if (body?.challenge === 'NEW_PASSWORD_REQUIRED' && body?.session) {
				navigate(`/new-password-required?username=${encodeURIComponent(username)}&session=${encodeURIComponent(body.session)}`)
				return
			}
		}

		// err-12: the login throttle (429) and server errors used to show
		// "Incorrect email or password", inviting more attempts
		if (response.status === 429) {
			setError('Too many login attempts. Please wait a few minutes and try again.')
			return
		}
		if (response.status >= 500) {
			setError('Something went wrong on our side. Please try again in a moment.')
			return
		}

		setError("Incorrect email or password. Please try again.")
	}

	useDocTitle('All for One - Log in')

	return (
		<section className="login-container">
			<h1 className="login-header">Hey <span className="highlight">there!</span></h1>

			<form
				id="login-form"
				action="#"
				method="POST"
				onSubmit={onFormSubmit}
			>
				<div className="form-group">
					<label htmlFor="username" className="visually-hidden"></label>
					<input
						type="text"
						id="username"
						name="username"
						placeholder="Username or email"
						required
						onChange={onUsernameBlur}
					/>
				</div>

				<div className="form-group">
					<label htmlFor="password" className="visually-hidden"></label>
					<input
						type="password"
						id="password"
						name="password"
						placeholder="Password"
						required
						onChange={onPasswordBlur}
					/>
					<Link to="/forgot-password" className="forgot-password">Forgot Password?</Link>
				</div>

				{error && <p className="login-error">{error}</p>}

				<div className="form-group">
					<button type="submit" className="login-btn">Log in</button>
				</div>
			</form>

			<p className="signup-prompt">
				Don't have an account? <Link to="/signup" className="signup-link">Sign up!</Link>
			</p>
		</section>
	)
}
